<template>
  <div
    class="align-top mb-7 d-flex justify-center"
    style="width: 100%;height: 100%;"
  >
    <div style="max-width: 1800px;">
      <div class="d-flex justify-center pa-4">
        <h1>Journal Analytics</h1>
      </div>
      <div class="pa-4">
        <h3 class="ma-4">
          Filters
        </h3>
        <div class="d-flex align-center pa-2">
          <span class="ma-2">
            Organizations:
          </span>
          <v-autocomplete
            v-model="filterOrganization"
            style="max-width: 500px;"
            chips
            clearable
            deletable-chips
            :items="organizations"
          />
        </div>
        <v-divider />
      </div>
      <div>
        <div class="d-flex justify-center pa-4" v-if="plotted">
          <h2>Overview</h2>
        </div>
        <div class="d-flex justify-center pa-4" v-if="plotted">
          <span>Report timestamp - {{ dateLastReport }}</span>
        </div>
        <div class="py-4 d-flex justify-center">
          <div class="px-4">
            <div id="journal-section-count" />
          </div>
          <div class="px-4">
            <div id="journal-type" />
          </div>
        </div>
        <div class="d-flex justify-center pa-4" v-if="plotted">
          <h2>Journal Health</h2>
        </div>
        <div class="py-4 d-flex justify-center">
          <div class="px-4">
            <div id="journal-with-rec" />
          </div>
          <div class="px-4">
            <div id="journal-lifespan-pie" />
          </div>
        </div>
        <div class="d-flex justify-center pa-4" v-if="plotted">
          <h2>Recommendation Analytics</h2>
        </div>
        <div class="py-4 d-flex justify-center">
          <div class="px-4">
            <div id="journal-interactions" />
          </div>
          <div class="px-4">
            <div id="journal-num-rec" />
          </div>
        </div>
        <div>
          <div class="d-flex justify-center pa-4" v-if="plotted">
            <h2>Recommendation Lifespan</h2>
          </div>
          <div class="pa-4">
            <div id="journal-lifespan-good" />
          </div>
          <div class="pa-4">
            <div id="journal-lifespan-bad" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { firestoreDocumentsByQuery, executeQuery } from '@/shared/firebase/firestore';
import Plotly from 'plotly.js-dist-min';
import Vue from 'vue';

export default {
  name: 'Analytics',
  data() {
    return {
      filterOrganization: undefined,
      organizations: [],
      reports: [],
      dateLastReport: undefined,
      plotted: false,
    };
  },
  async mounted() {
    console.log('Fetching journal reports');
    const query = firestoreDocumentsByQuery('journalReports', [], {
      field: 'date.timestamp',
      order: 'desc',
    }, 1);
    this.reports = await executeQuery(query);
    this.dateLastReport = new Date(this.reports[0].date.timestamp).toLocaleString();
  },
  methods: {
    reportName(report) {
      let tagName = report.section.metadata.organization_name;

      if (this.filterOrganization) {
        tagName = report.section.metadata.entity_name;
      } else {
        tagName = `${tagName} - ${report.section.metadata.entity_name}`;
      }

      const name = `${tagName} - ${report.section.name}`;
      if (name.length > 40) {
        return `${name.slice(0, 40)} ...`;
      }
      return name;
    },
    plotJournalInteractions(reports) {
      const data = reports.filter(report => report.num_rated > 0).map(report => {
        const name = this.reportName(report);

        return {
          x: ['num rated', 'avg rating', 'num opened'],
          y: [report.num_rated, report.avg_rating, report.num_opened],
          type: 'bar',
          name,
        };
      });

      const layout = { barmode: 'stack', title: 'Journal Interactions' };
      Plotly.newPlot('journal-interactions', data, layout);
    },
    plotJournalNumRec(reports) {
      const data = reports.filter(report => report.num_recommendations > 0).map(report => {
        const name = this.reportName(report);

        return {
          x: ['num recommendations'],
          y: [report.num_recommendations],
          type: 'bar',
          name,
        };
      });

      const layout = { title: 'Recommendation Number' };
      Plotly.newPlot('journal-num-rec', data, layout);
    },
    plotJournalWithRec(reports) {
      const withRec = reports.filter(report => report.num_recommendations > 0).length;
      const withoutRec = reports.filter(report => report.num_recommendations === 0).length;

      const data = [{
        values: [withRec, withoutRec],
        labels: ['With recommendations', 'Without recommendation'],
        type: 'pie',
      }];

      const layout = { title: 'Recommendation Number' };
      Plotly.newPlot('journal-with-rec', data, layout);
    },
    plotJournalSectionType(reports) {
      const reflection = reports.filter(report => report.section.metadata.entity_type === 'Reflection').length;
      const user = reports.filter(report => report.section.metadata.entity_type === 'User').length;
      const group = reports.filter(report => report.section.metadata.entity_type === 'Group').length;

      const data = [{
        values: [reflection, user, group],
        labels: ['Reflection', 'User', 'Group'],
        type: 'pie',
      }];

      const layout = { title: 'Journal Type' };
      Plotly.newPlot('journal-type', data, layout);
    },
    plotJournalUser(reports) {
      const dataMap = new Map();

      reports
        .filter(report => report.section.metadata.entity_type === 'User')
        .forEach(report => {
          const name = report.section.metadata.entity_name;

          if (dataMap.has(name)) {
            dataMap.set(name, dataMap.get(name) + 1);
          } else {
            dataMap.set(name, 1);
          }
        });

      const x = [];
      const y = [];

      [...dataMap.entries()].forEach(entry => {
        x.push(entry[0]);
        y.push(entry[1]);
      });

      const data = {
        x,
        y,
        type: 'bar',
      };
      const layout = {
        title: 'User Section',
        barmode: 'stack',
        yaxis: {
          title: 'Count',
        },
      };
      Plotly.newPlot('journal-section-count', [data], layout);
    },

    plotJournalLifespan(reports) {
      const lifespanInDays = (report) => {
        const lifespan = report.avg_recommendation_lifespan.amount / (1000 * 3600 * 24);
        return lifespan;
      };
      const toDataPoint = (report) => {
        const name = this.reportName(report);

        return {
          x: ['lifespan in days'],
          y: [lifespanInDays(report)],
          type: 'bar',
          name,
        };
      };

      const dataGood = reports
        .filter(report => lifespanInDays(report) < 7)
        .map(report => toDataPoint(report));

      const dataBad = reports
        .filter(report => lifespanInDays(report) >= 7)
        .map(report => toDataPoint(report));

      const layoutGood = { title: `Recommendation lifespan < 1 week (${dataGood.length}) sections` };
      const layoutBad = { title: `Recommendation lifespan >= 1 week (${dataBad.length}) sections` };


      const dataPie = [{
        values: [dataGood.length, dataBad.length],
        labels: ['Updated journal', 'Outdated journal'],
        type: 'pie',
      }];

      const layoutPie = { title: 'Journal Lifespan' };

      if (dataGood.length > 0) {
        Plotly.newPlot('journal-lifespan-good', dataGood, layoutGood);
      }

      if (dataBad.length > 0) {
        Plotly.newPlot('journal-lifespan-bad', dataBad, layoutBad);
      }

      Plotly.newPlot('journal-lifespan-pie', dataPie, layoutPie);
    },
    async plot() {
      this.plotted = false;
      await Vue.nextTick();

      let { reports } = this.reports[0];

      if (this.filterOrganization) {
        reports = reports
          .filter(report => report.section.metadata.organization_name === this.filterOrganization);
      }

      await Vue.nextTick();
      this.plotJournalInteractions(reports);

      await Vue.nextTick();
      this.plotJournalNumRec(reports);

      await Vue.nextTick();
      this.plotJournalLifespan(reports);

      await Vue.nextTick();
      this.plotJournalWithRec(reports);

      await Vue.nextTick();
      this.plotJournalSectionType(reports);

      await Vue.nextTick();
      this.plotJournalUser(reports);

      this.plotted = true;
    },
  },
  watch: {
    filterOrganization(oldValue, newValue) {
      if (oldValue !== newValue) {
        this.plot();
      }
    },
    reports() {
      this.organizations = this.reports[0].reports
        .filter(report => report.section)
        .filter(report => report.section.metadata)
        .map(report => report.section.metadata.organization_name);

      this.plot();
    },
  },
};
</script>
